/* eslint-disable react/prop-types */

import { useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, Row } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "src/common/axiosInstance";

interface OptionType {
    label: string;
    value: string;
}
interface OrderInfo {
    Status: string;
    client_report_certain_day: string;
    client_need_report: string;
    special_instructions: string;
    wrap_up_detail: string;
}
interface ReportingInvoicingModalProps {
    isOpen: boolean;
    toggle: () => void;
    client_day: string[];
    certain_day: OptionType[];
    wrap_deck: OptionType[];
    order_id: number;
    orderinfo: OrderInfo | null;
    onUpdate: (updatedOrderInfo: OrderInfo) => void;
}

const RepportingInvoicingModal: React.FC<ReportingInvoicingModalProps> = ({
    isOpen,
    toggle,
    client_day,
    certain_day,
    wrap_deck,
    order_id,
    orderinfo,
    onUpdate
}) => {
    const [clientNeedReport, setClientNeedReport] = useState('');
    const [specialInstructions, setSpecialInstructions] = useState("");
    const [selectedCertainDays, setSelectedCertainDays] = useState<OptionType[]>([]);
    const [selectedWrapDeck, setSelectedWrapDeck] = useState<OptionType[]>([]);
    // Reset fields when modal opens
    useEffect(() => {
        if (isOpen && orderinfo) {
            setClientNeedReport(orderinfo.client_need_report || "");
            setSpecialInstructions(orderinfo.special_instructions || "");

            // Convert comma-separated wrap_up_detail string into OptionType[]
            const wrapOptions = orderinfo.wrap_up_detail
                ? orderinfo.wrap_up_detail.split(',').map(item => ({
                    label: item.trim(),
                    value: item.trim()
                }))
                : [];
            setSelectedWrapDeck(wrapOptions);


            // Convert comma-separated certain days string into OptionType[]
            const certainDayOptions = orderinfo.client_report_certain_day
                ? JSON.parse(orderinfo.client_report_certain_day).map((day: string) => ({
                    label: day,
                    value: day
                }))
                : [];

            setSelectedCertainDays(certainDayOptions);
        }
    }, [isOpen, orderinfo]);

    const handleSave = async () => {
        if (!clientNeedReport) {
            toast.error("Please select how often do you need a report?");
            return;
        }
        const data = {
            clientNeedReport,
            specialInstructions,
            selectedCertainDays: selectedCertainDays.map(day => day.value), // ✅ just values
            selectedWrapDeck: selectedWrapDeck.map(item => item.value),
            order_id
        }
        const response = await axiosInstance.post("/update-reporting-invoicing", data);
        if (response.data.status) {
            const updatedData = response.data.data;
            updatedData.special_instructions = updatedData.Special_Instructions__c;
            updatedData.Status = orderinfo?.Status;
            delete updatedData.Special_Instructions__c;
            onUpdate(updatedData);

            toast.success("Reporting and invoice details saved successfully!");
            toggle()
        } else {
            toast.error("Something went wrong!");
        }
    };

    const reportOptions = [
        { value: 'Daily', label: 'Daily' },
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Bi-Weekly', label: 'Bi-Weekly' },
        { value: 'Monthly', label: 'Monthly' },
    ];

    return (
        <Modal size="lg" isOpen={isOpen} toggle={toggle} modalClassName={"general-custom-modal"}>
            <div className="modal-header">
                <h5 className="modal-title mt-0" id="myLargeModalLabel">
                    Reporting and Invoice Details
                </h5>
                <button
                    onClick={toggle}
                    type="button"
                    className="close"
                    aria-label="Close"
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
            <div className="modal-body">
                <Row>
                    <Col xl={12}>
                        {/* Client Report Frequency */}
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>
                                        <b>How often do you need a report?</b>
                                    </Label>
                                    <Select
                                        id="client_need_report"
                                        className="dropdown"
                                        classNamePrefix="react-select"
                                        isSearchable={false}
                                        options={reportOptions}
                                        placeholder="Select Day"
                                        value={reportOptions.find(opt => opt.value === clientNeedReport) || null}
                                        onChange={(selected: any) => {
                                            const selectedValue = selected?.value || '';
                                            setClientNeedReport(selectedValue);

                                            if (selectedValue === 'Daily') {
                                                setSelectedCertainDays(certain_day);
                                            } else {
                                                setSelectedCertainDays([]);
                                            }
                                        }}
                                        styles={{
                                            control: (provided: any) => ({
                                                ...provided,
                                                backgroundColor: 'transparent !important',
                                            }),
                                            menu: (base: any) => ({
                                                ...base,
                                                zIndex: 1000,
                                            }),
                                            menuList: (base: any) => ({
                                                ...base,
                                                padding: 0,
                                            }),
                                            option: (base: any, state: any) => ({
                                                ...base,
                                                backgroundColor: 'transparent',
                                                color: 'inherit',
                                                ':active': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }),
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Certain Day Selection */}
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>
                                        <b>
                                            Is there a certain day of the week they would like to
                                            receive reports?
                                        </b>
                                    </Label>
                                    <Select
                                        isMulti={true}
                                        placeholder={"Select Day"}
                                        options={certain_day}
                                        name="choices-multiple-skill-input"
                                        value={selectedCertainDays} // <-- selected values
                                        onChange={(selected: any) => setSelectedCertainDays(selected as OptionType[])} // <-- update state
                                        classNamePrefix="select2-selection form-control"
                                        styles={{
                                            menu: (provided: any) => ({
                                                ...provided,
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',

                                            }),
                                            input: (provided: any) => ({
                                                ...provided,
                                                fontSize: '16px',// stops ios zoom
                                            }),
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Wrap Up Deck */}
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>
                                        <b>
                                            What specific details is the client expecting on the
                                            Wrap Up Deck?
                                        </b>
                                    </Label>
                                    <Select
                                        isMulti
                                        options={wrap_deck}
                                        value={selectedWrapDeck}
                                        onChange={(selected: any) => setSelectedWrapDeck(selected as OptionType[])}
                                        name="choices-multiple-skill-input"
                                        classNamePrefix="select2-selection form-control"
                                        styles={{
                                             menu: (provided: any) => ({
                                                ...provided,
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',

                                            }),
                                            input: (provided: any) => ({
                                                ...provided,
                                                fontSize: '16px',// stops ios zoom
                                            }),
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Special Invoice Instructions */}
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>
                                        <b>Special Invoice Instructions:</b>
                                    </Label>
                                    <Input
                                        type="textarea"
                                        id="specialinstructions"
                                        rows="4"
                                        style={{ resize: "none" }}
                                        value={specialInstructions}
                                        onChange={(e) => setSpecialInstructions(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                {/* Save Button */}
                <Row className="mb-3">
                    <Col md={12} style={{ textAlign: "right" }}>
                        <button
                            type="button"
                            className="btn btn-primary button-primary w-md"
                            onClick={handleSave}
                        >
                            Save
                        </button>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

export default RepportingInvoicingModal;
